import classNames from "classnames";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { ReactComponent as ArrowSVG } from "assets/images/common/arrow.svg";
import { ReactComponent as EditIcon } from "assets/images/common/edit.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as VerifiedCheckIcon } from "assets/images/common/verified-check.svg";

import { useGuardedNavigation } from "hooks/useGuardedNavigation";
import PageTemplate from "modules/common/PageTemplate";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import Card from "components/Card";
import InfoCard from "components/InfoCard";
import Select from "components/Select";
import Switch from "components/Switch";

import { ModalType, UserRole } from "entities/common";
import { SubscriptionPortfolioItem } from "entities/notifications";
import { CompaniesApi, UsersApi } from "services/http";

import { RootStore } from "store";
import { useAppDispatch } from "store/hooks";
import { showModal } from "store/slices/modalSlice";
import { setNavBlocked } from "store/slices/navGuardSlice";
import {
  consumeWhatsappJustVerified,
  getSettingsRequest,
  getSubscriptionsRequest,
  saveAllRequest,
} from "store/slices/notificationSettingsSlice";

import styles from "./style.module.scss";

const NotificationsPage: FC = () => {
  const dispatch = useAppDispatch();
  const guardNavigation = useGuardedNavigation();
  const authUser = useSelector((store: RootStore) => store.auth.user);
  const isAdmin = useMemo(() => authUser?.role === UserRole.ADMINISTRATOR, [authUser?.role]);
  const [company, setCompany] = useState<string | undefined>(undefined);
  const [companyLabel, setCompanyLabel] = useState<string | undefined>(undefined);
  const [user, setUser] = useState<string | undefined>(undefined);
  const [userLabel, setUserLabel] = useState<string | undefined>(undefined);
  const [firstReminder, setFirstReminder] = useState<string | undefined>("30");
  const [secondReminder, setSecondReminder] = useState<string | undefined>("7");

  const { settings, subscriptions, whatsappJustVerified } = useSelector(
    (store: RootStore) => store.notificationSettings,
  );

  const targetUserId = useMemo(
    () => (isAdmin ? (user ? Number(user) : undefined) : authUser?.id),
    [isAdmin, user, authUser?.id],
  );

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [alertIaq, setAlertIaq] = useState(false);
  const [alertCalDue, setAlertCalDue] = useState(false);
  const [alertCalOverdue, setAlertCalOverdue] = useState(false);
  const [alertCompliance, setAlertCompliance] = useState(false);
  const [alertOffline, setAlertOffline] = useState(false);
  const [alertLowBattery, setAlertLowBattery] = useState(false);
  const [portfolioOn, setPortfolioOn] = useState<Record<number, boolean>>({});
  const [assetOn, setAssetOn] = useState<Record<number, boolean>>({});
  const [managedOnly, setManagedOnly] = useState(false);
  const seededUserIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (targetUserId) {
      dispatch(getSettingsRequest(targetUserId));
      dispatch(getSubscriptionsRequest(targetUserId));
    }
  }, [dispatch, targetUserId]);

  useEffect(() => {
    if (!settings || settings.user_id === seededUserIdRef.current) return;
    seededUserIdRef.current = settings.user_id;
    setEmailEnabled(settings.email_enabled);
    setWhatsappEnabled(settings.whatsapp_enabled);
    setAlertIaq(settings.alert_iaq_limit_exceeded);
    setAlertCalDue(settings.alert_calibration_due);
    setAlertCalOverdue(settings.alert_calibration_overdue);
    setAlertCompliance(settings.alert_compliance_status_change);
    setAlertOffline(settings.alert_device_offline);
    setAlertLowBattery(settings.alert_device_low_battery);
    setFirstReminder(String(settings.calibration_first_reminder_days));
    setSecondReminder(String(settings.calibration_second_reminder_days));
  }, [settings]);

  useEffect(() => {
    if (!subscriptions) return;
    const nextPortfolios: Record<number, boolean> = {};
    const nextAssets: Record<number, boolean> = {};
    subscriptions.portfolios.forEach((portfolio) => {
      nextPortfolios[portfolio.portfolio_id] = portfolio.enabled;
      portfolio.assets.forEach((asset) => {
        nextAssets[asset.asset_id] = asset.effective;
      });
    });
    setPortfolioOn(nextPortfolios);
    setAssetOn(nextAssets);
  }, [subscriptions]);

  useEffect(() => {
    if (!whatsappJustVerified) return;
    setWhatsappEnabled(true);
    dispatch(consumeWhatsappJustVerified());
  }, [whatsappJustVerified, dispatch]);

  const settingsDirty = useMemo(() => {
    if (!settings) return false;
    return (
      emailEnabled !== settings.email_enabled ||
      whatsappEnabled !== settings.whatsapp_enabled ||
      alertIaq !== settings.alert_iaq_limit_exceeded ||
      alertCalDue !== settings.alert_calibration_due ||
      alertCalOverdue !== settings.alert_calibration_overdue ||
      alertCompliance !== settings.alert_compliance_status_change ||
      alertOffline !== settings.alert_device_offline ||
      alertLowBattery !== settings.alert_device_low_battery ||
      Number(firstReminder) !== settings.calibration_first_reminder_days ||
      Number(secondReminder) !== settings.calibration_second_reminder_days
    );
  }, [
    settings,
    emailEnabled,
    whatsappEnabled,
    alertIaq,
    alertCalDue,
    alertCalOverdue,
    alertCompliance,
    alertOffline,
    alertLowBattery,
    firstReminder,
    secondReminder,
  ]);

  const subscriptionsDirty = useMemo(() => {
    if (!subscriptions) return false;
    return subscriptions.portfolios.some(
      (portfolio) =>
        (portfolioOn[portfolio.portfolio_id] ?? portfolio.enabled) !== portfolio.enabled ||
        portfolio.assets.some((asset) => (assetOn[asset.asset_id] ?? asset.effective) !== asset.effective),
    );
  }, [subscriptions, portfolioOn, assetOn]);

  const isDirty = settingsDirty || subscriptionsDirty;

  useEffect(() => {
    dispatch(setNavBlocked(isDirty));
  }, [isDirty, dispatch]);

  useEffect(() => () => void dispatch(setNavBlocked(false)), [dispatch]);

  useEffect(() => {
    if (!isAdmin) return;

    let cancelled = false;
    CompaniesApi.getList(
      QueryHelper.getQueryString({ [QUERY_PARAMS.PAGE]: 1, [QUERY_PARAMS.PAGE_SIZE]: 50 }),
    ).then(({ data }) => {
      const first = data?.items?.[0];
      if (cancelled || !first) return;
      setCompany(String(first.id));
      setCompanyLabel(first.name);
    });

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || !company) return;

    let cancelled = false;
    UsersApi.getList(
      QueryHelper.getQueryString({
        [QUERY_PARAMS.COMPANY_ID]: company,
        [QUERY_PARAMS.PAGE]: 1,
        [QUERY_PARAMS.PAGE_SIZE]: 50,
      }),
    ).then(({ data }) => {
      if (cancelled) return;
      const first = data?.items?.[0];
      setUser(first ? String(first.id) : undefined);
      setUserLabel(first ? first.full_name : undefined);
    });

    return () => {
      cancelled = true;
    };
  }, [isAdmin, company]);

  const options = useMemo(
    () => [
      { value: "60", label: "60 days before due date" },
      { value: "30", label: "30 days before due date" },
      { value: "14", label: "14 days before due date" },
      { value: "7", label: "7 days before due date" },
      { value: "3", label: "3 days before due date" },
      { value: "1", label: "1 day before due date" },
    ],
    [],
  );

  const firstReminderOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        disabled: Number(option.value) <= Number(secondReminder),
      })),
    [options, secondReminder],
  );

  const secondReminderOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        disabled: Number(option.value) >= Number(firstReminder),
      })),
    [options, firstReminder],
  );

  const handleCompanySearch = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 50,
        }),
      );

      return {
        options: data?.items?.map((company) => ({ value: String(company?.id), label: company?.name })) ?? [],
        hasMore: data?.total ? page * 50 < data?.total : false,
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const handleCompanyChange = useCallback(
    (value: string, label: string) => {
      guardNavigation(() => {
        setCompany(value);
        setCompanyLabel(label);
      });
    },
    [guardNavigation],
  );

  const handleUserSearch = useCallback(
    async (search: string, page: number) => {
      if (!company) return { options: [], hasMore: false };

      try {
        const { data } = await UsersApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.COMPANY_ID]: company,
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: 50,
          }),
        );

        return {
          options: data?.items?.map((user) => ({ value: String(user?.id), label: user?.full_name })) ?? [],
          hasMore: data?.total ? page * 50 < data?.total : false,
        };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [company],
  );

  const handleUserChange = useCallback(
    (value: string, label: string) => {
      guardNavigation(() => {
        setUser(value);
        setUserLabel(label);
      });
    },
    [guardNavigation],
  );

  const handleAddWhatsApp = () => {
    if (!targetUserId) return;
    dispatch(
      showModal({
        modalType: ModalType.WHATSAPP_VERIFICATION,
        modalProps: { userId: targetUserId, prefillPhone: settings?.phone ?? "" },
      }),
    );
  };

  const handleEditWhatsApp = () => {
    if (!targetUserId) return;
    dispatch(
      showModal({
        modalType: ModalType.WHATSAPP_VERIFICATION,
        modalProps: { userId: targetUserId, prefillPhone: settings?.whatsapp_phone ?? "" },
      }),
    );
  };

  const hasManaged = useMemo(
    () =>
      (subscriptions?.portfolios ?? []).some(
        (portfolio) => portfolio.is_manager || portfolio.assets.some((asset) => asset.managed),
      ),
    [subscriptions],
  );

  const handlePortfolioToggle = (portfolio: SubscriptionPortfolioItem, checked: boolean) => {
    setPortfolioOn((prev) => ({ ...prev, [portfolio.portfolio_id]: checked }));
    setAssetOn((prev) => {
      const next = { ...prev };
      portfolio.assets.forEach((asset) => {
        next[asset.asset_id] = checked;
      });
      return next;
    });
  };

  const handleAssetToggle = (portfolio: SubscriptionPortfolioItem, assetId: number, checked: boolean) => {
    setAssetOn((prev) => ({ ...prev, [assetId]: checked }));
    setPortfolioOn((prev) => {
      if (!checked) return { ...prev, [portfolio.portfolio_id]: false };
      const allOn = portfolio.assets.every((asset) =>
        asset.asset_id === assetId ? true : assetOn[asset.asset_id],
      );
      return allOn ? { ...prev, [portfolio.portfolio_id]: true } : prev;
    });
  };

  const handleSave = () => {
    if (!targetUserId) return;
    dispatch(
      saveAllRequest({
        userId: targetUserId,
        channels: { email_enabled: emailEnabled, whatsapp_enabled: whatsappEnabled },
        alertTypes: {
          alert_iaq_limit_exceeded: alertIaq,
          alert_calibration_due: alertCalDue,
          alert_calibration_overdue: alertCalOverdue,
          alert_compliance_status_change: alertCompliance,
          alert_device_offline: alertOffline,
          alert_device_low_battery: alertLowBattery,
          calibration_first_reminder_days: Number(firstReminder),
          calibration_second_reminder_days: Number(secondReminder),
        },
        subscriptions: {
          portfolios: (subscriptions?.portfolios ?? []).map((portfolio) => ({
            portfolio_id: portfolio.portfolio_id,
            enabled: portfolioOn[portfolio.portfolio_id] ?? portfolio.enabled,
          })),
          assets: (subscriptions?.portfolios ?? []).flatMap((portfolio) =>
            portfolio.assets.map((asset) => ({
              asset_id: asset.asset_id,
              enabled: assetOn[asset.asset_id] ?? asset.effective,
            })),
          ),
        },
      }),
    );
  };

  const saveLabel = isAdmin ? `Save & apply for "${userLabel ?? ""}"` : "Save & apply personally";

  return (
    <PageTemplate
      title="Notification settings"
      description={
        isAdmin
          ? "Configure how and where the user will receive alerts about incidents."
          : "Configure how and where you receive alerts about incidents."
      }
      isBackButton
      backButtonLink={PAGES.SETTINGS}
      className={styles.page}
    >
      <div className={styles.content}>
        <InfoCard size="small" variant="orange" color="orange" className={styles.warning}>
          <p className={styles.warningTitle}>Warning:</p>
          <p className={styles.warningDescription}>
            {isAdmin
              ? "These are personal settings. Changes apply only to a specific user and do not affect other users or the system configuration."
              : "These are personal settings. Changes apply only to your account and do not affect other users or the system configuration."}
          </p>
        </InfoCard>

        {isAdmin && (
          <Card className={styles.card}>
            <h2 className={styles.cardTitle}>Company & User</h2>
            <p className={styles.cardDescription}>
              Notification settings are saved separately for each user. Select the company, then the user you want to
              configure.
            </p>

            <div className={styles.fields}>
              <AsyncSearchableSelect
                label="Company"
                placeholder="Select company"
                value={company}
                selectedLabel={companyLabel}
                onSearch={handleCompanySearch}
                onChange={handleCompanyChange}
                className={styles.field}
              />

              <AsyncSearchableSelect
                label="User"
                placeholder="Select user"
                value={user}
                selectedLabel={userLabel}
                onSearch={handleUserSearch}
                onChange={handleUserChange}
                className={styles.field}
              />
            </div>
          </Card>
        )}

        <Card className={styles.card}>
          <h2 className={styles.cardTitle}>Notification channels</h2>
          <p className={styles.cardDescription}>
            {isAdmin ? "Choose how the user will receive notifications" : "Choose how you want to receive notifications"}
          </p>

          <div className={styles.fields}>
            <fieldset className={styles.fieldset}>
              <div className={styles.fieldsetHeader}>
                <p className={styles.fieldsetHeaderText}>Email</p>
                <div className={styles.fieldsetHeaderElement}>
                  <Switch checked={emailEnabled} onChange={setEmailEnabled} id="email" />
                </div>
              </div>

              <div className={styles.fieldsetContent}>
                <p className={styles.fieldsetContentText}>Receive alerts at your registered email address.</p>

                <div className={styles.fieldsetContentInfo}>
                  <span>{settings?.email}</span>
                  <span className={styles.fieldsetContentInfoIcon}>
                    <VerifiedCheckIcon />
                  </span>
                </div>
              </div>
            </fieldset>

            <fieldset className={classNames(styles.fieldset, styles.fieldsetDisabled)} disabled>
              <div className={styles.fieldsetHeader}>
                <p className={styles.fieldsetHeaderText}>WhatsApp</p>
                <div className={styles.fieldsetHeaderElement}>
                  {settings?.whatsapp_verified ? (
                    <Switch checked={whatsappEnabled} onChange={setWhatsappEnabled} id="whatsapp" />
                  ) : (
                    "Not verified"
                  )}
                </div>
              </div>

              <div className={styles.fieldsetContent}>
                <p className={styles.fieldsetContentText}>
                  Get instant alerts on WhatsApp — requires phone number verification.
                </p>

                {settings?.whatsapp_verified ? (
                  <div className={styles.fieldsetContentInfo}>
                    <span>{settings.whatsapp_phone}</span>
                    <button
                      type="button"
                      className={styles.editButton}
                      onClick={handleEditWhatsApp}
                      aria-label="Change WhatsApp number"
                    >
                      <EditIcon />
                    </button>
                  </div>
                ) : (
                  <Button variant="secondary" className={styles.fieldsetContentButton} onClick={handleAddWhatsApp}>
                    Add phone number
                  </Button>
                )}
              </div>

              <span className={styles.comingSoon} role="tooltip">
                Coming soon
              </span>
            </fieldset>
          </div>
        </Card>

        <Card className={styles.card}>
          <h2 className={styles.cardTitle}>Alert types</h2>
          <p className={styles.cardDescription}>
            {isAdmin
              ? "Choose which events trigger notifications to the user"
              : "Choose which events trigger notifications to you"}
          </p>

          <fieldset className={classNames(styles.fieldset, styles.alertTypes)}>
            <div className={styles.alertTypeField}>
              <Switch checked={alertIaq} onChange={setAlertIaq} id="iaq-limit-exceeded" />

              <div className={styles.alertTypeFieldContent}>
                <h3>IAQ limit exceeded</h3>

                <p>When any sensor reading exceeds a configured limit.</p>
              </div>
            </div>

            <div className={styles.alertTypeField}>
              <Switch checked={alertCalDue} onChange={setAlertCalDue} id="calibration-due" />

              <div className={styles.alertTypeFieldContent}>
                <h3>Calibration due</h3>

                <p>Scheduled calibration reminder for your sensor.</p>

                <div className={styles.alertTypeFieldSelects}>
                  <Select
                    options={firstReminderOptions}
                    onChange={setFirstReminder}
                    value={firstReminder}
                    label={"First reminder"}
                    className={styles.alertTypeFieldSelect}
                  />

                  <Select
                    options={secondReminderOptions}
                    onChange={setSecondReminder}
                    value={secondReminder}
                    label={"Second reminder"}
                    className={styles.alertTypeFieldSelect}
                  />
                </div>
              </div>
            </div>

            <div className={styles.alertTypeField}>
              <Switch checked={alertCalOverdue} onChange={setAlertCalOverdue} id="calibration-overdue" />

              <div className={styles.alertTypeFieldContent}>
                <h3>Calibration overdue</h3>

                <p>Calibration deadline has passed. Weekly reminder until resolved.</p>
              </div>
            </div>

            <div className={styles.alertTypeField}>
              <Switch checked={alertCompliance} onChange={setAlertCompliance} id="compliance-status-change" />

              <div className={styles.alertTypeFieldContent}>
                <h3>Compliance status change</h3>

                <p>An asset's compliance status has changed to non-compliant - review required.</p>
              </div>
            </div>

            <div className={styles.alertTypeField}>
              <Switch checked={alertOffline} onChange={setAlertOffline} id="device-offline" />

              <div className={styles.alertTypeFieldContent}>
                <h3>Device offline</h3>

                <p>Alert when device goes offline or stops reporting new values.</p>
              </div>
            </div>

            <div className={styles.alertTypeField}>
              <Switch checked={alertLowBattery} onChange={setAlertLowBattery} id="device-low-battery" />

              <div className={styles.alertTypeFieldContent}>
                <h3>Device low battery</h3>

                <p>Alert when device battery is low.</p>
              </div>
            </div>
          </fieldset>
        </Card>

        <Card className={styles.card}>
          <h2 className={styles.cardTitle}>Portfolio & asset subscriptions</h2>
          <p className={styles.cardDescription}>
            {isAdmin
              ? "Receive notifications for specific portfolios and assets. Assets where the user is assigned as manager are enabled by default."
              : "Receive notifications for specific portfolios and assets. Assets where you are assigned as manager are enabled by default."}
          </p>

          {subscriptions && subscriptions.portfolios.length === 0 ? (
            <div className={styles.empty}>
              <NotFoundIcon />
              <p>No portfolios yet</p>
            </div>
          ) : (
            <>
              <div className={styles.portfolios}>
                {subscriptions?.portfolios.map((portfolio) => {
                  const portfolioManaged =
                    portfolio.is_manager || portfolio.assets.some((asset) => asset.managed);

                  return (
                    <details
                      className={classNames(styles.portfolio, managedOnly && !portfolioManaged && styles.hidden)}
                      key={portfolio.portfolio_id}
                    >
                      <summary className={styles.portfolioSummary}>
                        <Switch
                          checked={portfolioOn[portfolio.portfolio_id] ?? portfolio.enabled}
                          onChange={(checked) => handlePortfolioToggle(portfolio, checked)}
                          id={`portfolio-${portfolio.portfolio_id}`}
                        />

                        <div className={styles.portfolioSummaryHeader}>
                          <div className={styles.portfolioSummaryHeaderTitle}>
                            <p className={styles.portfolioSummaryHeaderTitleText}>
                              <span>{portfolio.name}</span> {portfolio.is_manager && <span>Manager</span>}
                            </p>

                            <span className={styles.portfolioSummaryHeaderTitleIcon}>
                              <ArrowSVG />
                            </span>
                          </div>

                          <p className={styles.portfolioSummaryHeaderInfo}>
                            <span>Portfolio</span>
                            <span>{portfolio.assets.length} assets</span>
                          </p>
                        </div>
                      </summary>

                      <div className={styles.portfolioContent}>
                        {portfolio.assets.length === 0 ? (
                          <p className={styles.portfolioEmpty}>No assets in this portfolio</p>
                        ) : (
                          portfolio.assets.map((asset) => (
                            <div
                              className={classNames(
                                styles.portfolioContentItem,
                                managedOnly && !portfolio.is_manager && !asset.managed && styles.hidden,
                              )}
                              key={asset.asset_id}
                            >
                              <Switch
                                checked={assetOn[asset.asset_id] ?? asset.effective}
                                onChange={(checked) => handleAssetToggle(portfolio, asset.asset_id, checked)}
                                id={`asset-${asset.asset_id}`}
                              />

                              <p>
                                <span>{asset.name}</span>
                                {asset.managed && <span>Manager</span>}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </details>
                  );
                })}
              </div>

              {hasManaged && (
                <Button
                  variant="secondary"
                  className={styles.portfoliosButton}
                  onClick={() => setManagedOnly((prev) => !prev)}
                >
                  {managedOnly ? "Show all portfolios & assets" : "Show managed only"}
                </Button>
              )}
            </>
          )}
        </Card>

        <Button variant="primary" className={styles.saveButton} onClick={handleSave}>
          {saveLabel}
        </Button>
      </div>
    </PageTemplate>
  );
};

export default NotificationsPage;
